/**
 * Best-effort geolocation capture for attaching the player's actual GPS
 * position to submit / verify-geo requests. Used by the dashboard
 * activity feed so the organizer can see where a teammate actually was
 * when they fired an attempt, distinct from the coordinate the player
 * typed as their answer.
 *
 * Never throws, never blocks the submit flow. If the browser refuses,
 * the user denies permission, or the sensor times out, we resolve to
 * `undefined` and the caller sends the payload without position.
 *
 * 2026-04-26 user request — refusal throttle. Pre-fix every submit /
 * verify-geo call fired `getCurrentPosition` again, so a player who
 * denied permission once still saw the browser prompt on every action
 * («после каждого обновления экрана ему показывают окно запроса
 * разрешения ещё раз»). Now: track refusals in localStorage, after
 * `MAX_REFUSALS_BEFORE_GIVE_UP` denials skip the call entirely.
 */
const REFUSAL_KEY = 'aq_geo_refusals';
// 3 = give the player a chance to change their mind / fix browser
// settings, but stop after the third "Block" so the prompt isn't spam.
const MAX_REFUSALS_BEFORE_GIVE_UP = 3;
// Permission-error codes per W3C Geolocation API. Code 1 = PERMISSION_DENIED;
// codes 2 (POSITION_UNAVAILABLE) and 3 (TIMEOUT) are NOT permission refusals
// — those should not bump the refusal counter.
const ERR_PERMISSION_DENIED = 1;

function readRefusalCount(): number {
  try {
    const raw = localStorage.getItem(REFUSAL_KEY);
    if (!raw) return 0;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

function bumpRefusalCount(): void {
  try {
    const next = readRefusalCount() + 1;
    localStorage.setItem(REFUSAL_KEY, String(next));
  } catch { /* private mode / storage full — silently no-op */ }
}

/** Test/dev escape hatch: clear the refusal counter. Not exported in
 *  prod paths. Tests import via the test harness. */
export function _resetGeoRefusalCount(): void {
  try { localStorage.removeItem(REFUSAL_KEY); } catch { /* ignore */ }
}

export async function capturePlayerPosition(
  // 1200ms keeps the submit latency tolerable when geolocation stalls.
  // Cached positions (maximumAge: 30s) return in ~50ms; permission-granted
  // fresh lookups usually resolve under 500ms; denied/blocked contexts
  // fail almost instantly. The 1.2s ceiling is a best-effort wait — we'd
  // rather ship 📍-less than make the player stare at "Проверка..."
  // for three seconds on a flaky sensor.
  timeoutMs = 1200,
): Promise<{ lat: number; lon: number } | undefined> {
  // Same defensive check as the SOS path in game-menu.ts: jsdom and
  // restricted embeds expose navigator.geolocation with an undefined
  // getCurrentPosition. The `in navigator` check lies in those cases.
  const geo = typeof navigator !== 'undefined' ? navigator.geolocation : undefined;
  if (!geo || typeof geo.getCurrentPosition !== 'function') {
    return undefined;
  }
  // 3-strike refusal throttle: skip the call entirely once the player
  // has clicked "Block" enough times for the browser to keep remembering
  // it. The browser would auto-deny anyway, but calling getCurrentPosition
  // STILL pops a re-prompt on some browsers (Firefox, older Safari) when
  // the page reloads, which is the user-reported nuisance.
  if (readRefusalCount() >= MAX_REFUSALS_BEFORE_GIVE_UP) {
    return undefined;
  }
  return new Promise((resolve) => {
    // Belt-and-suspenders timeout — some browsers (older Safari, certain
    // WebViews) never fire error callbacks when location is suppressed.
    const safetyTimer = setTimeout(() => resolve(undefined), timeoutMs + 500);
    try {
      geo.getCurrentPosition(
        (pos) => {
          clearTimeout(safetyTimer);
          resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        },
        (err) => {
          clearTimeout(safetyTimer);
          // Only bump the counter on PERMISSION_DENIED (code 1). A
          // POSITION_UNAVAILABLE (code 2 — sensor down) or TIMEOUT
          // (code 3 — slow GPS lock) is NOT a refusal; the player
          // didn't actively decline, so don't spend their refusal
          // budget on transient errors.
          if (err && err.code === ERR_PERMISSION_DENIED) {
            bumpRefusalCount();
          }
          resolve(undefined);
        },
        { timeout: timeoutMs, enableHighAccuracy: true, maximumAge: 30000 },
      );
    } catch {
      clearTimeout(safetyTimer);
      resolve(undefined);
    }
  });
}
